import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { CircleNotch, Flask, Plus, UsersThree, X } from "@phosphor-icons/react";
import { PeoplePicker } from "@/components/consumption/PeoplePicker";
import { MaterialPicker } from "@/components/consumption/MaterialPicker";
import { StockItemPicker } from "@/components/consumption/StockItemPicker";
import { WeldingRobotPanel } from "@/components/consumption/WeldingRobotPanel";
import { api, registerError } from "@/components/consumption/registerApi";

// Jun 2026, user's ask: "Allow Department Head to add process, Add Supervisor,
// Add Operator only their plant. But Department always assigned by the admin
// with their site and Department." So this panel is everything a head may
// change about his own department - and nothing else. The department itself,
// its site, its cost centre and who the head is stay with the admin.
const ProcessCard = ({ process, items, stock, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [searchAny, setSearchAny] = useState(false);

  const addMaterial = async (picked) => {
    setBusy(true);
    try {
      await api.mapItem({
        process_id: process.id, product_id: picked.product_id,
        description: picked.description, unit_code: picked.uom,
      });
      toast.success(`${picked.product_id} added to ${process.name}`);
      await onChanged();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const removeMaterial = async (item) => {
    try {
      await api.unmapItem(item.id);
      toast.success(`${item.product_id} removed from ${process.name}`);
      await onChanged();
    } catch (err) {
      toast.error(registerError(err).message);
    }
  };

  const retire = async () => {
    if (!confirmRetire) { setConfirmRetire(true); return; }
    try {
      await api.updateProcess(process.id, { active: false });
      toast.success(`${process.name} retired - the figures already recorded stay in the register.`);
      await onChanged();
    } catch (err) {
      toast.error(registerError(err).message);
    }
  };

  return (
    <div className="rounded-sm border border-[#EAECF0] bg-[#F9FAFB] p-3" data-testid={`self-process-${process.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading text-sm font-bold text-[#101828] truncate">{process.name}</div>
          <div className="text-[11px] text-[#667085]">
            {items.length ? `${items.length} material(s) recorded here` : "No material yet - nothing can be recorded until one is added"}
          </div>
        </div>
        <button
          onClick={retire}
          onBlur={() => setConfirmRetire(false)}
          className={`shrink-0 text-[11px] font-bold px-2 py-1 rounded-sm border transition-colors duration-150 ${
            confirmRetire
              ? "bg-[#FEF3F2] border-[#FDA29B] text-[#B42318]"
              : "bg-white border-[#D0D5DD] text-[#667085] hover:text-[#B42318] hover:border-[#FDA29B]"
          }`}
          data-testid={`self-process-retire-${process.id}`}
        >
          {confirmRetire ? "Tap again to retire" : "Retire"}
        </button>
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {items.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#D0D5DD] pl-2 pr-1 py-0.5 text-[11px]"
              data-testid={`self-item-${item.id}`}
            >
              <span className="font-mono font-bold text-[#101828]">{item.product_id}</span>
              <span className="text-[#667085] truncate max-w-[160px]">{item.description || ""}{item.unit_code ? ` · ${item.unit_code}` : ""}</span>
              <button
                onClick={() => removeMaterial(item)}
                className="p-0.5 rounded-full text-[#98A2B3] hover:text-[#B42318] hover:bg-[#FEF3F2] transition-colors duration-150"
                data-testid={`self-item-remove-${item.id}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 space-y-1.5">
        <StockItemPicker
          stock={stock}
          mappedProductIds={items.map((i) => i.product_id)}
          onPick={addMaterial}
          busy={busy}
          testIdPrefix={`self-item-${process.id}`}
        />
        {searchAny ? (
          <MaterialPicker onPick={addMaterial} busy={busy} testIdPrefix={`self-item-${process.id}`} />
        ) : (
          <button
            onClick={() => setSearchAny(true)}
            className="text-[11px] text-[#667085] underline underline-offset-2 hover:text-[#0E7C86] transition-colors duration-150"
            data-testid={`self-item-${process.id}-search-any`}
          >
            Need something not in this godown? Search any material
          </button>
        )}
      </div>
    </div>
  );
};

export const DepartmentSelfSetup = ({ department, processes, items, onChanged }) => {
  const [people, setPeople] = useState([]);
  const [stock, setStock] = useState(null);
  const [operators, setOperators] = useState(department.member_emails || []);
  const [supervisors, setSupervisors] = useState(department.supervisor_emails || []);
  const [savingPeople, setSavingPeople] = useState(false);
  const [newProcess, setNewProcess] = useState("");
  const [addingProcess, setAddingProcess] = useState(false);

  useEffect(() => {
    api.people().then(setPeople).catch(() => setPeople([]));
  }, []);

  useEffect(() => {
    api.departmentStock(department.id).then(setStock).catch(() => setStock(null));
  }, [department.id]);

  useEffect(() => {
    setOperators(department.member_emails || []);
    setSupervisors(department.supervisor_emails || []);
  }, [department.id, department.member_emails, department.supervisor_emails]);

  const savePeople = async () => {
    setSavingPeople(true);
    try {
      await api.updateDepartment(department.id, { member_emails: operators, supervisor_emails: supervisors });
      toast.success(`Saved - ${department.name} now has ${operators.length} operator(s) and ${supervisors.length} supervisor(s).`);
      await onChanged();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setSavingPeople(false);
    }
  };

  const addProcess = async () => {
    const name = newProcess.trim();
    if (!name) return;
    setAddingProcess(true);
    try {
      await api.createProcess({ department_id: department.id, name });
      toast.success(`${name} added - map the chemicals it burns next.`);
      setNewProcess("");
      await onChanged();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setAddingProcess(false);
    }
  };

  const managerNames = (department.manager_emails || [])
    .map((email) => people.find((p) => p.email === email)?.name || email);
  // 3 Nov 2026, user: "add welding robots page under my department so that user
  // can set up setting self".
  const isWelding = /WEL/.test(`${department.cost_center_id || ""} ${department.name || ""}`.toUpperCase());

  return (
    <div className="space-y-3" data-testid="department-self-setup">
      <div className="rounded-sm border border-[#D0D5DD] bg-white p-4">
        <div className="flex items-center gap-2">
          <UsersThree size={18} weight="fill" className="text-[#0E7C86]" />
          <h2 className="font-heading text-base md:text-lg font-bold text-[#101828]">Who works in {department.name}</h2>
        </div>
        <p className="text-sm text-[#667085] mt-0.5">
          {department.site_id} only. The department itself, its cost centre and its head are set by an administrator.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <Label className="text-xs font-bold text-[#344054]">Dept. manager</Label>
            <div className="mt-1 h-9 flex items-center px-3 rounded-md border border-[#EAECF0] bg-[#F9FAFB] text-sm text-[#475467] truncate" data-testid="self-setup-manager">
              {managerNames.length ? managerNames.join(", ") : "Not assigned"}
            </div>
            <p className="text-[11px] text-[#667085] mt-1">Assigned by the administrator.</p>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Operators</Label>
            <PeoplePicker
              people={people}
              value={operators}
              onChange={setOperators}
              placeholder="Select operators"
              testId="self-setup-operators"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Supervisors</Label>
            <PeoplePicker
              people={people}
              value={supervisors}
              onChange={setSupervisors}
              placeholder="Select supervisors"
              testId="self-setup-supervisors"
            />
          </div>
        </div>

        <Button
          onClick={savePeople}
          disabled={savingPeople}
          className="mt-3 h-9 bg-[#0E7C86] hover:bg-[#0B6B74] text-sm"
          data-testid="self-setup-save-people"
        >
          {savingPeople ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null}
          Save people
        </Button>
      </div>

      <div className="rounded-sm border border-[#D0D5DD] bg-white p-4">
        <div className="flex items-center gap-2">
          <Flask size={18} weight="fill" className="text-[#0E7C86]" />
          <h2 className="font-heading text-base md:text-lg font-bold text-[#101828]">Processes and their materials</h2>
        </div>
        <p className="text-sm text-[#667085] mt-0.5">
          A process is where the chemical is burned - plating line, powder booth, phosphating tank.
        </p>
        {stock?.logistics_area_id && (
          <p className="text-[11px] text-[#667085] mt-0.5" data-testid="self-setup-stock-note">
            Materials offered are the consumables SAP holds in <span className="font-mono font-bold text-[#344054]">{stock.logistics_area_id}</span>
            {" "}- {stock.items.length} in stock{stock.as_of ? `, as of ${new Date(stock.as_of).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}.
            {stock.other_category_count > 0 && ` ${stock.other_category_count} other line(s) in this godown are raw material or finished goods, so they are not offered here.`}
          </p>
        )}

        <div className="flex gap-2 mt-3">
          <Input
            value={newProcess}
            onChange={(e) => setNewProcess(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addProcess()}
            placeholder="New process name"
            className="h-9 text-sm"
            data-testid="self-setup-process-name"
          />
          <Button
            onClick={addProcess}
            disabled={addingProcess || !newProcess.trim()}
            className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74] text-sm shrink-0"
            data-testid="self-setup-add-process"
          >
            {addingProcess ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
            <span className="ml-1 hidden sm:inline">Add process</span>
          </Button>
        </div>

        <div className="space-y-2 mt-3">
          {processes.length === 0 ? (
            <p className="text-sm text-[#B54708] font-medium" data-testid="self-setup-no-process">
              No process yet - add the first one above, then map what it consumes.
            </p>
          ) : (
            processes.map((p) => (
              <ProcessCard
                key={p.id}
                process={p}
                items={items.filter((i) => i.process_id === p.id)}
                stock={stock}
                onChanged={onChanged}
              />
            ))
          )}
        </div>
      </div>

      {isWelding && <WeldingRobotPanel isAdmin={false} lockedSite={department.site_id} />}
    </div>
  );
};
